import {
  cloneElement,
  isValidElement,
  memo,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useNodesData, useNodeId } from '@xyflow/react'
import { createNodeData } from '@/shared/lib/node-registry'
import { cn } from '@/shared/lib/utils'
import { useCustomNodeData } from '@/entities/node/lib/useCustomNodeData'

interface InlineEditableNodeTitleProps {
  icon?: ReactNode
  title?: string
}

const titleIconClassName = 'h-4 w-4 shrink-0 text-white/52'

function renderTitleIcon(icon: ReactNode) {
  if (!isValidElement<{ className?: string }>(icon)) {
    return icon
  }

  return cloneElement(icon as ReactElement<{ className?: string }>, {
    className: cn(titleIconClassName, icon.props.className),
  })
}

export const InlineEditableNodeTitle = memo(function InlineEditableNodeTitle({
  icon,
  title: titleOverride,
}: InlineEditableNodeTitleProps) {
  const nodeId = useNodeId()
  const flowNodeData = useNodesData(nodeId ?? '')
  const nodeType = flowNodeData?.type ?? 'default'
  const defaultTitle = createNodeData(nodeType).title
  const fallbackTitle = typeof defaultTitle === 'string' ? defaultTitle : ''
  const { nodeData, snapshot } = useCustomNodeData()
  const title = snapshot.title
  const value = titleOverride ?? (typeof title === 'string' ? title : fallbackTitle)
  const [localValue, setLocalValue] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const editing = localValue !== null
  const titleIcon = renderTitleIcon(icon)

  useEffect(() => {
    if (!editing) {
      return
    }

    inputRef.current?.focus()
    inputRef.current?.select()
  }, [editing])

  const cancelEditing = () => {
    setLocalValue(null)
  }

  const updateTitle = (nextValue: string) => {
    // Valtio proxy mutation is the established node data update path.
    // eslint-disable-next-line react-hooks/immutability
    nodeData.title = nextValue
  }

  const commitEditing = () => {
    const nextValue = (localValue ?? value).trim()

    if (!nextValue) {
      setLocalValue(null)

      if (fallbackTitle !== value) {
        updateTitle(fallbackTitle)
      }
      return
    }

    if (nextValue !== value) {
      updateTitle(nextValue)
    }

    setLocalValue(null)
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitEditing()
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditing()
    }
  }

  if (editing) {
    return (
      <div className="flex h-7 max-w-full items-center gap-2 text-white/82">
        {titleIcon}
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(event) => setLocalValue(event.currentTarget.value)}
          onBlur={commitEditing}
          onKeyDown={handleKeyDown}
          onPointerDown={(event) => event.stopPropagation()}
          className={cn(
            'nodrag nopan h-6 min-w-[104px] max-w-[220px] rounded-md border border-white/26 bg-[#161619] px-2 text-[13px] font-medium text-white/90 outline-none',
            'h-7 min-w-[96px] max-w-[220px] text-[16px] font-medium text-white/92 flex-shrink-0',
          )}
        />
      </div>
    )
  }

  return (
    <div
      onDoubleClick={(event) => {
        event.stopPropagation()
        setLocalValue(value)
      }}
      className="nodrag nopan flex h-7 max-w-full cursor-text select-none items-center gap-2 text-white/82"
      title="双击修改标题"
    >
      {titleIcon}
      <span className="truncate max-w-[180px] text-[16px] font-medium text-white/92">
        {value}
      </span>
    </div>
  )
})
